import type { PushDeliveryMetaReason } from '@/services/db/delivery/utils';

/**
 * Groups a delivery's per-device push statuses into the buckets the results
 * accordion renders, ported from the Ember `push-action-results` component's
 * `deliveryGroups` getter. A device is `sent` when its `status` is 200;
 * `undeliverable` / `bounced` / `suppressed` come off their boolean flags; and
 * everything not already in one of those four buckets is `failed` (Ember's
 * "not already categorized" set difference). Empty buckets are dropped.
 *
 * The `drafted` bucket (Ember's `recipientDevices` branch) is deferred to
 * FPS-3178 — it needs a new customer-devices data-layer read.
 */

const SUCCESS_STATUS = 200;

export type PushDeliveryGroupType = 'sent' | 'undeliverable' | 'bounced' | 'suppressed' | 'failed';

export interface PushDeliveryGroup {
  type: PushDeliveryGroupType;
  devices: PushDeliveryMetaReason[];
}

function sentDevices(devices: PushDeliveryMetaReason[]): PushDeliveryMetaReason[] {
  return devices.filter((device) => device.status === SUCCESS_STATUS);
}

function undeliverableDevices(devices: PushDeliveryMetaReason[]): PushDeliveryMetaReason[] {
  return devices.filter((device) => device.undeliverable === true);
}

function bouncedDevices(devices: PushDeliveryMetaReason[]): PushDeliveryMetaReason[] {
  return devices.filter((device) => device.bounced === true);
}

function suppressedDevices(devices: PushDeliveryMetaReason[]): PushDeliveryMetaReason[] {
  return devices.filter((device) => device.suppressed === true);
}

/** Everything not sent / undeliverable / bounced / suppressed (Ember's set difference). */
function failedDevices(devices: PushDeliveryMetaReason[]): PushDeliveryMetaReason[] {
  const categorized = new Set<PushDeliveryMetaReason>([
    ...sentDevices(devices),
    ...undeliverableDevices(devices),
    ...bouncedDevices(devices),
    ...suppressedDevices(devices),
  ]);
  return devices.filter((device) => !categorized.has(device));
}

export function groupDeviceStatuses(devices: PushDeliveryMetaReason[]): PushDeliveryGroup[] {
  const groups: PushDeliveryGroup[] = [
    { type: 'sent', devices: sentDevices(devices) },
    { type: 'undeliverable', devices: undeliverableDevices(devices) },
    { type: 'bounced', devices: bouncedDevices(devices) },
    { type: 'suppressed', devices: suppressedDevices(devices) },
    { type: 'failed', devices: failedDevices(devices) },
  ];
  return groups.filter((group) => group.devices.length > 0);
}
